import { PlusOutlined } from '@ant-design/icons';
import { Button, Flex, Input, Modal, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreviewInfo, PreviewUpdateData } from '@shared/api';
import { Delete } from '@shared/assets/icons/components';
import { ConfirmModal } from '@shared/ui/confirm-modal';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { normalizePreviewValues } from '../lib/preview-values';
import styles from '../preview.module.scss';

const MAX_ANSWERS = 5;

interface PreviewEditModalProps {
  preview: PreviewInfo | null;
  isUpdating: boolean;
  isDeleting: boolean;
  onClose: () => void;
  onSave: (previewId: number, values: PreviewUpdateData) => Promise<void>;
  onDelete: (previewId: number) => Promise<void>;
}

export const PreviewEditModal = ({
  preview,
  isUpdating,
  isDeleting,
  onClose,
  onSave,
  onDelete,
}: PreviewEditModalProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'preview' });
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<string[]>(['']);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const isBusy = isUpdating || isDeleting;

  useEffect(() => {
    if (!preview) return;

    setQuestion(preview.question);
    setAnswers(preview.answers.length ? preview.answers : ['']);
    setIsSubmitted(false);
    setIsDeleteConfirmOpen(false);
  }, [preview]);

  const values = useMemo(() => normalizePreviewValues(question, answers), [answers, question]);
  const initialValues = useMemo(
    () => normalizePreviewValues(preview?.question ?? '', preview?.answers ?? []),
    [preview],
  );
  const isValid =
    Boolean(values.question) && values.answers.length > 0 && values.answers.every(Boolean);
  const isChanged =
    values.question !== initialValues.question ||
    values.answers.length !== initialValues.answers.length ||
    values.answers.some((answer, index) => answer !== initialValues.answers[index]);

  const handleClose = () => {
    if (isBusy) return;
    setIsDeleteConfirmOpen(false);
    onClose();
  };

  const handleSave = async () => {
    setIsSubmitted(true);
    if (!preview || !isValid) return;

    await onSave(preview.id, values);
  };

  const handleDelete = async () => {
    if (!preview) return;

    await onDelete(preview.id);
    setIsDeleteConfirmOpen(false);
  };

  const updateAnswer = (index: number, value: string) => {
    setAnswers((current) =>
      current.map((answer, answerIndex) => (answerIndex === index ? value : answer)),
    );
  };

  const removeAnswer = (index: number) => {
    if (answers.length === 1) return;
    setAnswers((current) => current.filter((_, answerIndex) => answerIndex !== index));
  };

  const addAnswer = () => {
    setAnswers((current) => (current.length < MAX_ANSWERS ? [...current, ''] : current));
  };

  return (
    <>
      <Modal
        centered
        open={Boolean(preview)}
        title={t('edit.title')}
        width={512}
        className={styles.editModal}
        maskClosable={!isBusy}
        keyboard={!isBusy}
        closable={!isBusy}
        onCancel={handleClose}
        footer={
          <div className={styles.modalActions}>
            <Button
              danger
              size="large"
              disabled={isBusy}
              className={styles.deleteButton}
              onClick={() => setIsDeleteConfirmOpen(true)}
            >
              {t('edit.delete')}
            </Button>
            <Button
              type="primary"
              size="large"
              loading={isUpdating}
              disabled={isBusy || !isChanged}
              onClick={handleSave}
            >
              {t('edit.save')}
            </Button>
          </div>
        }
      >
        <ScrollContainer height="auto" maxHeight="calc(100vh - 220px)" marginRight="0">
          <Flex vertical gap={20} className={styles.modalBody}>
            <label className={styles.field}>
              <Typography.Text strong>{t('edit.url')}</Typography.Text>
              <Input
                value={preview?.url ?? ''}
                readOnly
                disabled={isBusy}
                title={preview?.url}
                onFocus={(event) => event.currentTarget.select()}
              />
            </label>

            <label className={styles.field}>
              <Typography.Text strong>{t('edit.question')}</Typography.Text>
              <Input.TextArea
                value={question}
                disabled={isBusy}
                status={isSubmitted && !values.question ? 'error' : undefined}
                autoSize={{ minRows: 4, maxRows: 8 }}
                onChange={(event) => setQuestion(event.target.value)}
              />
              {isSubmitted && !values.question && (
                <Typography.Text type="danger" className={styles.fieldError}>
                  {t('edit.questionRequired')}
                </Typography.Text>
              )}
            </label>

            <div className={styles.field}>
              <Typography.Text strong>{t('edit.answers')}</Typography.Text>
              <ScrollContainer
                height="auto"
                maxHeight="200px"
                marginRight="0"
                style={{ flex: '0 1 auto' }}
              >
                <Flex vertical gap={8} className={styles.answersList}>
                  {answers.map((answer, index) => {
                    const hasError = isSubmitted && !answer.trim();

                    return (
                      <div key={index}>
                        <Flex gap={8} align="start">
                          <Input
                            value={answer}
                            disabled={isBusy}
                            status={hasError ? 'error' : undefined}
                            onChange={(event) => updateAnswer(index, event.target.value)}
                          />
                          <Button
                            danger
                            type="text"
                            icon={<Delete />}
                            disabled={isBusy || answers.length === 1}
                            className={styles.answerDeleteButton}
                            aria-label={t('edit.deleteAnswer', { number: index + 1 })}
                            onClick={() => removeAnswer(index)}
                          />
                        </Flex>
                        {hasError && (
                          <Typography.Text type="danger" className={styles.fieldError}>
                            {t('edit.answerRequired')}
                          </Typography.Text>
                        )}
                      </div>
                    );
                  })}
                </Flex>
              </ScrollContainer>
              <Button
                block
                icon={<PlusOutlined />}
                disabled={isBusy || answers.length >= MAX_ANSWERS}
                onClick={addAnswer}
              >
                {t('edit.add')}
              </Button>
            </div>
          </Flex>
        </ScrollContainer>
      </Modal>

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        title={t('delete.title')}
        text={
          <span>
            {t('delete.text')} <strong className={styles.confirmUrl}>{preview?.url}</strong>?
          </span>
        }
        confirmButtonLabel={t('delete.confirm')}
        cancelButtonLabel={t('delete.cancel')}
        confirmButtonDanger
        isLoading={isDeleting}
        closable={!isDeleting}
        keyboard={!isDeleting}
        maskClosable={!isDeleting}
        onConfirm={handleDelete}
        onClose={() => !isDeleting && setIsDeleteConfirmOpen(false)}
        onCancelButton={() => setIsDeleteConfirmOpen(false)}
      />
    </>
  );
};
